/**
 * context/CountryContext.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Client-side country-scope context for the admin dashboard. Mirrors the
 * PermissionsProvider / EntitlementProvider pattern and is the display-layer
 * companion to the backend's `enforceStaffCountry` middleware.
 *
 * It seeds `homeCountry` from the logged-in user's profile
 * (`getUserFromLocalStorage().result.country`, default 'UG' for legacy
 * sessions), computes the single canonical "may switch country" capability, and
 * exposes the active market to the tree.
 *
 * Exposes (via useCountry()):
 *   - activeCountry       String  — ISO-2 whose data is currently shown.
 *   - homeCountry         String  — the user's assigned/home ISO-2.
 *   - isSuperAdmin        Boolean — canonical super-admin flag.
 *   - canSwitch           Boolean — may this identity switch countries?
 *   - availableCountries  Array   — countries this identity may select.
 *   - market              Market  — the registry entry for activeCountry.
 *   - setActiveCountry(iso2)       — super: switch + persist; non-super: no-op.
 *
 * ADVISORY, not a boundary. Non-supers are LOCKED to their home country here so
 * the UI never even offers another market — but the SERVER remains the real
 * gate (`enforceStaffCountry` ignores/403s a spoofed `x-country`).
 *
 * Canonical predicate (do NOT scatter role-string checks — three overlapping
 * role fields exist): may switch ⇔ isSuperAdmin === true || user_role ===
 * "SUPERUSER". Defined once here as `computeCanSwitch`.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  DEFAULT_COUNTRY,
  SUPPORTED_COUNTRIES,
  getMarket,
  normalizeCountry,
} from "../config/markets.js";
import { getUserFromLocalStorage } from "../utilities/user.js";
import {
  getActiveCountry,
  setActiveCountry as persistActiveCountry,
} from "../utilities/activeCountry.js";

const CountryContext = createContext(null);

/**
 * The single canonical "may switch country" capability. Reads the two accepted
 * signals off the cached profile's `result` object. Keep this the ONLY place
 * that decides switch-capability on the client.
 * @param {Object|null} result - getUserFromLocalStorage().result
 * @returns {boolean}
 */
export const computeCanSwitch = (result) => {
  if (!result) return false;
  if (result.isSuperAdmin === true) return true;
  return String(result.user_role || "").toUpperCase() === "SUPERUSER";
};

/** Read the user's home country from the cached profile (default 'UG'). */
const readHomeCountry = (result) =>
  normalizeCountry(result?.country) || DEFAULT_COUNTRY;

export const CountryProvider = ({ children }) => {
  // Derive the initial identity snapshot once, synchronously, so the first
  // paint already has the right market (no flash of the wrong country).
  const [state, setState] = useState(() => {
    const result = getUserFromLocalStorage()?.result || null;
    const homeCountry = readHomeCountry(result);
    const canSwitch = computeCanSwitch(result);
    // Non-super identities are LOCKED to their home country regardless of any
    // persisted/derived active value. Supers use the persisted selection.
    const activeCountry = canSwitch ? getActiveCountry() : homeCountry;
    // Keep the module-level store aligned with what we render so the axios
    // interceptor stamps the same country on the very first request.
    persistActiveCountry(activeCountry);
    return {
      homeCountry,
      canSwitch,
      isSuperAdmin: canSwitch,
      activeCountry,
    };
  });

  // Re-seed the snapshot from the profile — used on mount and when auth changes
  // in another tab (login/logout/refresh rotate `profile`/`token`).
  const reseed = useCallback(() => {
    const result = getUserFromLocalStorage()?.result || null;
    const homeCountry = readHomeCountry(result);
    const canSwitch = computeCanSwitch(result);
    const activeCountry = canSwitch ? getActiveCountry() : homeCountry;
    persistActiveCountry(activeCountry);
    setState({
      homeCountry,
      canSwitch,
      isSuperAdmin: canSwitch,
      activeCountry,
    });
  }, []);

  useEffect(() => {
    reseed();
    // Cross-tab sync: re-seed on auth change, and adopt a sibling tab's country
    // switch so all tabs of a super-admin agree on the active market.
    const onStorage = (e) => {
      if (e.key === "token" || e.key === "profile") {
        reseed();
      } else if (e.key === "activeCountry") {
        setState((s) => {
          if (!s.canSwitch) return s; // non-super stays locked
          const next = normalizeCountry(e.newValue) || s.homeCountry;
          return next === s.activeCountry ? s : { ...s, activeCountry: next };
        });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [reseed]);

  // Super-admin country switch. For non-supers this is a hard no-op — the UI
  // never renders a switcher for them, and even a stray call cannot widen scope.
  const setActiveCountry = useCallback((iso2) => {
    setState((s) => {
      if (!s.canSwitch) return s;
      const next = normalizeCountry(iso2);
      if (!next || next === s.activeCountry) return s;
      // Write through the module-level store so the axios interceptor (and
      // sibling tabs, via the storage event) pick up the new country.
      persistActiveCountry(next);
      return { ...s, activeCountry: next };
    });
  }, []);

  const value = useMemo(() => {
    const { activeCountry, homeCountry, isSuperAdmin, canSwitch } = state;
    const availableCountries = canSwitch
      ? [...SUPPORTED_COUNTRIES]
      : [homeCountry];
    return {
      activeCountry,
      homeCountry,
      isSuperAdmin,
      canSwitch,
      availableCountries,
      market: getMarket(activeCountry),
      setActiveCountry,
    };
  }, [state, setActiveCountry]);

  return (
    <CountryContext.Provider value={value}>{children}</CountryContext.Provider>
  );
};

/**
 * React hook — reads from CountryContext. Returns a safe, UG-defaulted shape if
 * called outside the provider (never throws) so a stray mount cannot crash the
 * dashboard. `setActiveCountry` is a no-op in that fallback.
 */
export const useCountry = () => {
  const ctx = useContext(CountryContext);
  if (!ctx) {
    return {
      activeCountry: DEFAULT_COUNTRY,
      homeCountry: DEFAULT_COUNTRY,
      isSuperAdmin: false,
      canSwitch: false,
      availableCountries: [DEFAULT_COUNTRY],
      market: getMarket(DEFAULT_COUNTRY),
      setActiveCountry: () => {},
    };
  }
  return ctx;
};

export default CountryContext;
